import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Button,
  CardMedia,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Input,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useAddPromotionMutation, useUpdatePromotionMutation, useGetPromotionByIdQuery } from "../../services/promotions";
import { useGetUserListQuery } from "../../services/user";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/uploadMedia";
import { PROMOTION_REWARD_TYPE, PROMOTION_TYPE } from "../../constants/enums";
import { isNumber } from "../../utils/validations";
import EditText from "../../components/editText";
import usePermission from "../../hooks/userAccess";

const usersList = [
  { label: "Select All Users", id: "all" },
  { label: "John Doe", id: "1" },
  { label: "Jane Smith", id: "2" },
  { label: "Michael Scott", id: "3" },
  { label: "Pam Beesly", id: "4" },
];

const AddPromotion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdd, isDelete, isView } = usePermission({
    label: 'Promotion Management',
    page_permission: 'isAdd',
  })

  const { id: routeId } = useParams();
  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(true);
  const [selectedUsers, setSelectedUsers] = React.useState<typeof usersList>([]);
  const { data: usersRes } = useGetUserListQuery({ page: 1, limit: 1000, search: "" });
  const dynamicUsers = useMemo(() => {
    const list = (usersRes as any)?.data?.list || (Array.isArray((usersRes as any)?.data) ? (usersRes as any).data : []);
    return list.map((u: any) => ({ label: u?.fullName || u?.email || u?._id, id: u?._id })) as typeof usersList;
  }, [usersRes]);

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [fileName, setFileName] = useState<string>("");
  const [legal2, setLegal2] = useState<string>("");
  const [legal2Ar, setLegal2Ar] = useState<string>("");

  const isAddPromotion = location.pathname === "/manage-promotions/add";
  const isEdit = Boolean(routeId);
  const { data: promoByIdRes, isFetching: isFetchingPromo } = useGetPromotionByIdQuery(
    isEdit ? { id: String(routeId) } : ({ id: '' } as any),
    { skip: !isEdit }
  );
  const editingData = useMemo(() => {
    if (promoByIdRes && (promoByIdRes as any).data) return (promoByIdRes as any).data;
    return (location as any)?.state?.promotion || null;
  }, [promoByIdRes, location]);

  useEffect(() => {
    // Prefill from existing promotion when editing
    if (isEdit) {
      const tc = (editingData as any)?.termsConditions || "";
      setLegal2(tc);
      const tcAr = (editingData as any)?.termsConditions_ar || "";
      setLegal2Ar(tcAr);
    }
  }, [isEdit, editingData]);

  const [addPromotion, { isLoading: isSaving }] = useAddPromotionMutation();
  const [updatePromotion, { isLoading: isUpdating }] = useUpdatePromotionMutation();

  // Enhanced validation schema
  const validationSchema = Yup.object({
    image: Yup.string().required("Image is required"),
    title: Yup.string()
      .required("Title is required")
      .min(3, "Title must be at least 3 characters")
      .max(100, "Title must be less than 100 characters"),
    title_ar: Yup.string()
      .required("Arabic title is required")
      .min(3, "Arabic title must be at least 3 characters")
      .max(100, "Arabic title must be less than 100 characters"),
    type: Yup.number()
      .required("Type is required")
      .min(1, "Please select a valid type")
      .max(2, "Please select a valid type"),
    promotionType: Yup.number()
      .required("Promotion type is required")
      .min(1, "Please select a valid promotion type"),
    loyaltyPoints: Yup.number()
      .required("Loyalty points are required")
      .min(0, "Loyalty points cannot be negative")
      .max(10000, "Loyalty points cannot exceed 10,000"),
    startFrom: Yup.string()
      .required("Start date is required")
      .test("is-future", "Start date must be in the future", function (value) {
        if (!value) return false;
        return dayjs(value).isAfter(dayjs().subtract(1, 'minute'));
      }),
    expireAt: Yup.string()
      .required("End date is required")
      .test("is-after-start", "End date must be after start date", function (value) {
        const { startFrom } = this.parent;
        if (!value || !startFrom) return false;
        return dayjs(value).isAfter(dayjs(startFrom));
      })
      .test("minimum-duration", "Promotion must last at least 1 hour", function (value) {
        const { startFrom } = this.parent;
        if (!value || !startFrom) return false;
        return dayjs(value).diff(dayjs(startFrom), 'hour') >= 1;
      }),
    ctaLabel: Yup.string()
      .required("CTA label is required")
      .min(2, "CTA label must be at least 2 characters")
      .max(50, "CTA label must be less than 50 characters"),
    isDeleted: Yup.boolean(),
    maxRedemptions: Yup.number()
      .required("Max redemptions is required")
      .min(1, "Max redemptions must be at least 1")
      .max(100000, "Max redemptions cannot exceed 100,000"),
    category: Yup.number()
      .required("Category is required")
      .min(1, "Please select a valid category"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(1000, "Description must be less than 1000 characters"),
    description_ar: Yup.string()
      .required("Arabic description is required")
      .min(10, "Arabic description must be at least 10 characters")
      .max(1000, "Arabic description must be less than 1000 characters"),
    termsConditions: Yup.string()
      .required("Terms & Conditions are required")
      .min(20, "Terms & Conditions must be at least 20 characters")
      .max(2000, "Terms & Conditions must be less than 2000 characters"),
    termsConditions_ar: Yup.string()
      .required("Arabic Terms & Conditions are required")
      .min(20, "Arabic Terms & Conditions must be at least 20 characters")
      .max(2000, "Arabic Terms & Conditions must be less than 2000 characters"),
    url: Yup.string()
      .url("Please enter a valid URL")
      .nullable()
      .optional(),
    users: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one user")
      .required("Target audience is required"),
    id: Yup.string().optional(),
  });

  // Initial values
  const getInitialValues = () => ({
    image: editingData?.image || "",
    title: editingData?.title || "",
    title_ar: editingData?.title_ar || "",
    type: PROMOTION_REWARD_TYPE.PROMOTIONS,
    promotionType: Number(editingData?.promotionType) || PROMOTION_TYPE.APP_ONLY,
    loyaltyPoints: Number(editingData?.loyaltyPoints) || 0,
    startFrom: editingData?.startFrom ? dayjs(editingData.startFrom).toISOString() : "",
    expireAt: editingData?.expireAt ? dayjs(editingData.expireAt).toISOString() : "",
    ctaLabel: editingData?.ctaLabel || "",
    isDeleted: Boolean(editingData?.isDeleted) || false,
    maxRedemptions: Number(editingData?.maxRedemptions) || 1,
    category: Number(editingData?.category) || 1,
    description: editingData?.description || "",
    description_ar: editingData?.description_ar || "",
    termsConditions: editingData?.termsConditions || "",
    termsConditions_ar: editingData?.termsConditions_ar || "",
    url: editingData?.url || "",
    users: Array.isArray(editingData?.users) ? editingData.users : [],
  });

  // Handle users change for autocomplete
  const handleUsersChange = (setFieldValue: any, currentUsers: string[]) =>
    (event: any, value: typeof usersList) => {
      const isSelectAllClicked = value.some((user) => user.id === "all");

      if (isSelectAllClicked) {
        const isAllSelected = currentUsers.length === usersList.length - 1;

        if (isAllSelected) {
          // Uncheck all
          setFieldValue('users', []);
          setSelectedUsers([]);
        } else {
          // Select all (excluding "Select All" option itself)
          const allUserIds = usersList.filter((user) => user.id !== "all").map(u => u.id);
          setFieldValue('users', allUserIds);
          setSelectedUsers(usersList.filter((user) => user.id !== "all"));
        }
      } else {
        const userIds = value.map(u => u.id);
        setFieldValue('users', userIds);
        setSelectedUsers(value);
      }
    };

  const handleImageUpload = async (file: any, setFieldValue: any) => {
    const res = await UploadMedia(file);
    const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
    if (imageUrl) {
      setFieldValue('image', imageUrl);
    }
  }

  return (
    <>
      <div className="main_title">
        <h1>{isAddPromotion ? "Add " : "Edit "} Promotion</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate(-1)}>
            Promotion Management
          </button>{" "}
          - {isAddPromotion ? "Add " : "Edit "} Promotion
        </p>
      </div>

      <div className="cards">
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, setFieldError }) => {
            try {
              setSubmitting(true);

              if (!legal2?.length || !legal2Ar?.length) {
                return
              }

              if (isAddPromotion) {
                await addPromotion({ ...(values as any), termsConditions: legal2, termsConditions_ar: legal2Ar } as any).unwrap();
              } else {
                await updatePromotion({ body: { ...values, termsConditions: legal2, termsConditions_ar: legal2Ar, id: routeId } as any }).unwrap();
              }

              navigate(-1);
            } catch (error: any) {
              // Handle specific field errors if API returns them
              if (error?.data?.errors) {
                Object.keys(error.data.errors).forEach((field) => {
                  setFieldError(field, error.data.errors[field]);
                });
              } else {
                setAlertType(0);
                setShowAlert(true);
                setAlertMessage("An error occurred while saving. Please try again.");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue, setFieldTouched, handleSubmit, isSubmitting }) => (
            <Form className="form" onSubmit={handleSubmit}>
              <div className="gap_p">
                {/* Image Upload */}
                <div className="control_group w_100">
                  <div className="gap_p">
                    <div className="control_group w_33">
                      <label htmlFor="icon-button-file">
                        Upload Image <span style={{ color: 'red' }}>
                          *
                        </span>
                      </label>
                      {values.image ? (
                        <div className="upload_image v2 no_radius">
                          <div className="upload_image_holder">
                            <figure>
                              <CardMedia
                                component="img"
                                image={values.image}
                                alt="Uploaded promotion image"
                              />
                            </figure>
                            <IconButton
                              aria-label="Remove uploaded image"
                              onClick={() => {
                                setFieldValue('image', '');
                                setFileName("");
                              }}
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        <label
                          className="upload_image v2 no_radius"
                          htmlFor="icon-button-file"
                        >
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{ accept: "image/png,image/jpeg,image/jpg" }}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              const files = (event.target as HTMLInputElement).files;
                              if (files && files[0]) {
                                if (files[0].type.includes("image")) {
                                  // Validate file size (max 5MB)
                                  if (files[0].size > 5 * 1024 * 1024) {
                                    setAlertType(0);
                                    setShowAlert(true);
                                    setAlertMessage("Image size must be less than 5MB.");
                                    return;
                                  }
                                  setFileName(files[0].name);
                                  handleImageUpload(files[0], setFieldValue)
                                } else {
                                  setAlertType(0);
                                  setShowAlert(true);
                                  setAlertMessage("This field only accepts images (PNG, JPEG, JPG).");
                                }
                              }
                            }}
                          />
                          <span className="upload_image_holder">
                            <figure className="add_img">
                              <img
                                src="/static/images/placeholder.png"
                                alt="placeholder"
                              />
                              <span className="upload_img_icon">
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="Upload"
                                />
                              </span>
                            </figure>
                          </span>
                        </label>
                      )}
                      {touched.image && errors.image && (
                        <FormHelperText error>{String(errors.image)}</FormHelperText>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="control_group w_33">
                  <label htmlFor="title">
                    Title <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="title"
                    name="title"
                    placeholder="Enter Title"
                    hiddenLabel
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title ? String(errors.title) : ""}
                  />
                </div>

                {/* Arabic Title */}
                <div className="control_group w_33">
                  <label htmlFor="title_ar">
                    Arabic Title <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="title_ar"
                    name="title_ar"
                    placeholder="Enter Arabic Title"
                    hiddenLabel
                    fullWidth
                    value={values.title_ar}
                    onChange={handleChange}
                    error={touched.title_ar && Boolean(errors.title_ar)}
                    helperText={touched.title_ar && errors.title_ar ? String(errors.title_ar) : ""}
                  />
                </div>

                {/* Promotion Type */}
                <div className="control_group w_33">
                  <label htmlFor="promotionType">
                    Promotion Type <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    id="promotionType"
                    name="promotionType"
                    fullWidth
                    value={values.promotionType}
                    onChange={handleChange}
                    error={touched.promotionType && Boolean(errors.promotionType)}
                  >
                    <MenuItem value={PROMOTION_TYPE.APP_ONLY}>App only</MenuItem>
                    <MenuItem value={PROMOTION_TYPE.IN_STORE}>In-store</MenuItem>
                  </Select>
                  {touched.promotionType && errors.promotionType && (
                    <FormHelperText error>{String(errors.promotionType)}</FormHelperText>
                  )}
                </div>

                {/* URL */}
                <div className="control_group w_33">
                  <label htmlFor="url">
                    URL <span> (for external articles)</span>
                  </label>
                  <TextField
                    id="url"
                    name="url"
                    placeholder="Enter URL (https://...)"
                    hiddenLabel
                    fullWidth
                    value={values.url}
                    onChange={handleChange}
                    error={touched.url && Boolean(errors.url)}
                    helperText={touched.url && errors.url ? String(errors.url) : ""}
                  />
                </div>

                {/* CTA Label */}
                <div className="control_group w_33">
                  <label htmlFor="ctaLabel">
                    CTA Label <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="ctaLabel"
                    name="ctaLabel"
                    placeholder="Enter CTA Label"
                    hiddenLabel
                    fullWidth
                    value={values.ctaLabel}
                    onChange={handleChange}
                    error={touched.ctaLabel && Boolean(errors.ctaLabel)}
                    helperText={touched.ctaLabel && errors.ctaLabel ? String(errors.ctaLabel) : ""}
                  />
                </div>

                {/* Loyalty Points */}
                <div className="control_group w_33">
                  <label htmlFor="loyaltyPoints">
                    Loyalty Points <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="loyaltyPoints"
                    name="loyaltyPoints"
                    placeholder="Enter Loyalty Points"
                    hiddenLabel
                    fullWidth
                    value={values.loyaltyPoints}
                    onChange={(val) => {
                      if (isNumber(val.target.value)) {
                        handleChange(val)
                      }
                    }}
                    error={touched.loyaltyPoints && Boolean(errors.loyaltyPoints)}
                    helperText={touched.loyaltyPoints && errors.loyaltyPoints ? String(errors.loyaltyPoints) : ""}
                    inputProps={{ min: 0, max: 10000 }}
                  />
                </div>

                {/* Max Redemptions */}
                <div className="control_group w_33">
                  <label htmlFor="maxRedemptions">
                    Max Redemptions <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="maxRedemptions"
                    name="maxRedemptions"
                    placeholder="Enter Max Redemptions"
                    hiddenLabel
                    fullWidth
                    value={values.maxRedemptions}
                    onChange={(val) => {
                      if (isNumber(val.target.value)) {
                        handleChange(val)
                      }
                    }}
                    error={touched.maxRedemptions && Boolean(errors.maxRedemptions)}
                    helperText={touched.maxRedemptions && errors.maxRedemptions ? String(errors.maxRedemptions) : ""}
                    inputProps={{ min: 1, max: 100000 }}
                  />
                </div>

                {/* Schedule Promotion */}
                <div className="control_group w_100">
                  <label htmlFor="schedule">
                    Schedule Promotion <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className="gap_p">
                    <div className="control_group w_50">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          className="form_control"
                          slots={{
                            openPickerIcon: () => <img src="/static/images/date_icon.svg" alt="Pick date" />,
                          }}
                          slotProps={{
                            textField: {
                              id: "startFrom",
                              placeholder: "Select Start Date & Time",
                              fullWidth: true,
                              hiddenLabel: true,
                              error: touched.startFrom && Boolean(errors.startFrom),
                              helperText: touched.startFrom && errors.startFrom ? String(errors.startFrom) : "",
                            },
                          }}
                          desktopModeMediaQuery="(min-width:0px)"
                          value={values.startFrom ? dayjs(values.startFrom) : null}
                          onChange={(v) => setFieldValue('startFrom', v ? (v as Dayjs).toISOString() : '')}
                          minDateTime={dayjs()}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="control_group w_50">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          className="form_control"
                          slots={{
                            openPickerIcon: () => <img src="/static/images/date_icon.svg" alt="Pick date" />,
                          }}
                          slotProps={{
                            textField: {
                              id: "expireAt",
                              placeholder: "Select End Date & Time",
                              fullWidth: true,
                              hiddenLabel: true,
                              error: touched.expireAt && Boolean(errors.expireAt),
                              helperText: touched.expireAt && errors.expireAt ? String(errors.expireAt) : "",
                            },
                          }}
                          desktopModeMediaQuery="(min-width:0px)"
                          value={values.expireAt ? dayjs(values.expireAt) : null}
                          onChange={(v) => setFieldValue('expireAt', v ? (v as Dayjs).toISOString() : '')}
                          minDateTime={values.startFrom ? dayjs(values.startFrom).add(1, 'hour') : dayjs().add(1, 'hour')}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>

                {/* Target Audience */}
                <div className="control_group">
                  <label htmlFor="users">
                    Target Audience <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={[{ label: "Select All Users", id: "all" }, ...dynamicUsers]}
                    value={[{ label: "Select All Users", id: "all" }, ...dynamicUsers].filter((u) => values.users.includes(u.id))}
                    onChange={handleUsersChange(setFieldValue, values.users as any)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onOpen={() => setAutoCompleteOpen(true)}
                    onClose={() => setAutoCompleteOpen(false)}
                    renderOption={(props, option, { selected }) => {
                      const isAllOption = option.id === "all";
                      const isAllSelected = values.users.length === dynamicUsers.length;

                      return (
                        <li {...props}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isAllOption ? isAllSelected : selected}
                                indeterminate={
                                  isAllOption &&
                                  values.users.length > 0 &&
                                  !isAllSelected
                                }
                              />
                            }
                            name="option"
                            label={option.label}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Users"
                        variant="outlined"
                        error={touched.users && Boolean(errors.users)}
                        helperText={touched.users && errors.users ? String(errors.users) : ""}
                      />
                    )}
                  />
                </div>

                {/* Description */}
                <div className="control_group">
                  <label htmlFor="description">
                    Description <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="description"
                    name="description"
                    minRows={5}
                    multiline
                    placeholder="Enter Description (min 10 characters)"
                    hiddenLabel
                    fullWidth
                    value={values.description}
                    onChange={handleChange}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description ? String(errors.description) : ""}
                  />
                </div>

                {/* Arabic Description */}
                <div className="control_group">
                  <label htmlFor="description_ar">
                    Arabic Description <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="description_ar"
                    name="description_ar"
                    minRows={5}
                    multiline
                    placeholder="Enter Arabic Description (min 10 characters)"
                    hiddenLabel
                    fullWidth
                    value={values.description_ar}
                    onChange={handleChange}
                    error={touched.description_ar && Boolean(errors.description_ar)}
                    helperText={touched.description_ar && errors.description_ar ? String(errors.description_ar) : ""}
                  />
                </div>

                {/* Terms & Conditions */}
                <div className="control_group">
                  <label htmlFor="termsConditions">
                    Terms & Conditions <span style={{ color: 'red' }}>*</span>
                  </label>
                  {isEdit && !legal2 ? null : <EditText content={legal2 || ' '} setContent={(val: string) => { setLegal2(val); setFieldValue('termsConditions', val); setFieldTouched('termsConditions', true, true); }} />}
                  {touched.termsConditions && errors.termsConditions && (
                    <FormHelperText error>{String(errors.termsConditions)}</FormHelperText>
                  )}
                </div>

                {/* Arabic Terms & Conditions */}
                <div className="control_group">
                  <label htmlFor="termsConditions_ar">
                    Arabic Terms & Conditions <span style={{ color: 'red' }}>*</span>
                  </label>
                  {isEdit && !legal2Ar ? null : (
                    <EditText
                      content={legal2Ar || ' '}
                      setContent={(val: string) => {
                        setLegal2Ar(val);
                        setFieldValue('termsConditions_ar', val);
                        setFieldTouched('termsConditions_ar', true, true);
                      }}
                    />
                  )}
                  {touched.termsConditions_ar && errors.termsConditions_ar && (
                    <FormHelperText error>{String(errors.termsConditions_ar)}</FormHelperText>
                  )}
                </div>
              </div>

              {isAdd && <div className="form_btn">
                <Button
                  className="btn btn_primary"
                  type="submit"
                  disabled={isSaving || isUpdating || isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AddPromotion;